import { useCallback } from 'react';
import type { SessionSummary } from '../../../types/analyticstypes';
import { formatLocalDate, formatLocalDateTime, formatSec, formatPrice } from '../../../utils/analytics/format';
import { downloadCSV } from '../../../utils/analytics/downloadCSV';

interface ExportSessionsCSVProps {
  sessions: SessionSummary[];
}

export default function ExportSessionsCSV({ sessions }: ExportSessionsCSVProps) {
  const handleExport = useCallback(() => {
    if (sessions.length === 0) return;

    const headers = [
      'Store',
      'Device',
      'Time',
      'Browse',
      'Kitchen',
      'Items',
      'Extras',
      'Total',
      'Result',
      'Rating',
      'Loyalty',
      'Order #',
    ];

    const rows = sessions.map((s) => {
      const adds = s.addOnCount ?? 0;
      const ups = s.upsellCount ?? 0;
      const extrasParts = [];
      if (adds > 0) extrasParts.push(`${adds} Add-on${adds !== 1 ? 's' : ''}`);
      if (ups > 0) extrasParts.push(`${ups} Upsell${ups !== 1 ? 's' : ''}`);
      
      let result = s.status || '';
      if (s.status === 'COMPLETED') {
        result = s.paymentStatus === 'PAID' ? 'PAID' : 'UNPAID';
      } else if (s.status === 'ABANDONED') {
        result = s.endReason === 'timeout' ? 'TIMEOUT' : s.endReason === 'cancel' ? 'CANCELLED' : 'ABANDONED';
      }

      let rating = '';
      if (s.status !== 'ABANDONED' && s.feedbackRating != null && s.feedbackMaxRating) {
        rating = `${s.feedbackRating}/${s.feedbackMaxRating}`;
      }

      const loyaltyParts = [];
      if (s.loyaltyCustomer) loyaltyParts.push('Loyalty');
      if (s.loyaltyEnrollAttempted) loyaltyParts.push('Enroll');

      return [
        s.storeNumber || '',
        s.deviceId || '',
        `${formatLocalDate(s.timestamp)} ${formatLocalDateTime(s.timestamp)}`,
        s.journeyCorrupt ? 'Corrupt' : (s.browseDurationSec != null ? formatSec(s.browseDurationSec) : ''),
        s.kdsDurationSec != null ? formatSec(s.kdsDurationSec) : '',
        s.itemCount ?? '',
        extrasParts.join(', '),
        s.totalPrice != null ? formatPrice(s.totalPrice) : '',
        result,
        rating,
        loyaltyParts.join(', '),
        s.orderNumber || '',
      ];
    });

    const filename = `sessions_export_${new Date().toISOString()}.csv`;
    downloadCSV(filename, headers, rows);
  }, [sessions]);

  if (sessions.length === 0) return null;

  return (
    <button
      onClick={handleExport}
      className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
    >
      Export CSV
    </button>
  );
}
